import { Component, ContentChild, EventEmitter, Input, Output, TemplateRef } from '@angular/core';

import { BaseView } from '../../base-view';
import { Form } from '@fitogest/reactive-form-builder/interfaces';
import { RequestHandler } from '../../../service/OffService/request-handler';
import { GenerateEnvironmentDefinition } from './environment-variables';

import { BaseResponseModel } from 'src/app/models/base-response.model';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { HistoricoREAModel, ReaImportData, ReaModalModel } from 'src/app/models/fincas/rea.model';


export interface ModalChartDataset {
  label: string;
  data: number[];
  fill: boolean;
  borderColor: string;
  backgroundColor: string;
  tension: number;
}

export interface ModalChartData {
  labels: string[];
  datasets: ModalChartDataset[];
}

export type ModalChartType = 'line' | 'bar' | 'doughnut' | 'pie' | 'scatter' | 'bubble' | 'polarArea' | 'radar';

export interface ModalChartConfiguration {
  modalKey: string;
  title: string;
  type: ModalChartType;
  data: ModalChartData;
  options: Record<string, unknown>;
  loading: boolean;
  emptyMessage: string;
}

/**
 * Generic modal host backed by BaseView.
 * It centralizes shared dialogs so feature components only open them through view.show(...).
 */
@Component({
  selector: 'app-modals',
  standalone: false,
  templateUrl: './modals.component.html',
  styleUrls: ['./modals.component.scss']
})
export class ModalsComponent {
  /** View contract used to control visibility and actions. */
  @Input() public view!: BaseView<any>;

  /** Reactive form-builder inputs shared by info/delete dialogs. */
  @Input() public form: Form<any> | undefined;

  @Input() public serverUrl!: string;

  @Input() public previousData!: any;

  /** Extra flags/requests used by delete dialog. */
  @Input() public recordHasAssociatedData = false;

  @Input() public getRequest!: RequestHandler<any[]>;

  @Input() public deleteRequest!: RequestHandler<any>;

  @Input() public displayWizard = false;

  /** Optional reusable chart dialog configuration. */
  @Input() public chartConfiguration: ModalChartConfiguration | null = null;

  /** Optional REA history used by the finca REA dialog. */
  @Input() public historicoREA: BaseResponseModel<HistoricoREAModel> | null = null;

  /** Emitted when a child modal changes records and the parent list must be refreshed. */
  @Output() public readonly refreshRequested = new EventEmitter<void>();

  /** Optional content projected before the reactive form (kept for backward compatibility). */
  @ContentChild('content', { read: TemplateRef, static: false })
  public content?: TemplateRef<unknown>;

  /** Optional content projected before the reactive form. */
  @ContentChild('beforeFormContent', { read: TemplateRef, static: false })
  public beforeFormContent?: TemplateRef<unknown>;

  /** Optional content projected after the reactive form. */
  @ContentChild('afterFormContent', { read: TemplateRef, static: false })
  public afterFormContent?: TemplateRef<unknown>;

  /** Optional content projected inside the transactions file-generation dialog. */
  @ContentChild('generateFileContent', { read: TemplateRef, static: false })
  public generateFileContent?: TemplateRef<unknown>;

  /** Emitted when the generated file dialog is closed by any available action. */
  @Output() public readonly generateFileClosed = new EventEmitter<void>();

  public readonly env: GenerateEnvironmentDefinition = new GenerateEnvironmentDefinition();

  /** Reusable modal key used by optional chart dialogs. */
  public get chartModalKey(): string {
    return this.chartConfiguration?.modalKey ?? 'grafica';
  }

  /** True when the reusable chart has at least one dataset with visible values. */
  public get hasChartData(): boolean {
    const data = this.chartConfiguration?.data;

    return !!data?.labels?.length && data.datasets.some((dataset: ModalChartDataset): boolean => dataset.data.length > 0);
  }

  /** Current finca when app-modals is used by the finca module. */
  public get finca(): FincasModel | null {
    return (this.previousData ?? null) as unknown as FincasModel | null;
  }

  /** REA child modal payload derived from the selected finca. */
  public get reaModel(): ReaModalModel {
    const finca = this.finca;

    return {
      codigo_REA: finca?.codigo_REA,
      titular_nif: finca?.nif ?? finca?.titular_nif ?? '',
      nombre_finca: finca?.nombre ?? '',
      id_cliente: finca?.id_cliente,
    };
  }

  /** Scroll to top when a dialog becomes visible. */
  public scrollToTop(): void {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      /* no-op */
    }
  }

  /** Keep BaseView as the single source of truth for modal visibility. */
  public setDialogVisibility(modal: string, visible: boolean): void {
    if (visible) {
      this.view.canShow[modal] = true;
      return;
    }

    this.view.hide(modal);
  }

  /** Notify the parent component so it can reset generated file form state. */
  public onGenerateFileClosed(): void {
    this.generateFileClosed.emit();
  }

  /** Refresh parent list after a successful REA import. */
  public onReaImported(result: BaseResponseModel<ReaImportData>): void {
    if (result.statusCode >= 200 && result.statusCode < 300) {
      this.refreshRequested.emit();
    }
  }
}
